/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter
} from '@angular/core';



import DxScrollView from 'devextreme/ui/scroll_view';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';







/**
 * The ScrollView is a widget that enables a user to scroll its content.
 */

export class DxScrollViewComponent extends DxComponent implements OnDestroy {
    instance: DxScrollView;


    /**
     * A Boolean value specifying whether to enable or disable the bounce-back effect.
     */
    
    get bounceEnabled(): boolean {
        return this._getOption('bounceEnabled');
    }
    set bounceEnabled(value: boolean) {
        this._setOption('bounceEnabled', value);
    }


    /**
     * A string value specifying the available scrolling directions.
     */
    
    get direction(): string {
        return this._getOption('direction');
    }
    set direction(value: string) {
        this._setOption('direction', value);
    }


    /**
     * A Boolean value specifying whether or not the widget can respond to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies the text shown in the pullDown panel when pulling the content down lowers the refresh threshold.
     */
    
    get pulledDownText(): string {
        return this._getOption('pulledDownText');
    }
    set pulledDownText(value: string) {
        this._setOption('pulledDownText', value);
    }


    /**
     * Specifies the text shown in the pullDown panel while pulling the content down to the refresh threshold.
     */
    
    get pullingDownText(): string {
        return this._getOption('pullingDownText');
    }
    set pullingDownText(value: string) {
        this._setOption('pullingDownText', value);
    }


    /**
     * Specifies the text shown in the pullDown panel displayed when content is scrolled to the bottom.
     */
    
    get reachBottomText(): string {
        return this._getOption('reachBottomText');
    }
    set reachBottomText(value: string) {
        this._setOption('reachBottomText', value);
    }


    /**
     * Specifies the text shown in the pullDown panel displayed when the content is being refreshed.
     */
    
    get refreshingText(): string {
        return this._getOption('refreshingText');
    }
    set refreshingText(value: string) {
        this._setOption('refreshingText', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * A Boolean value specifying whether or not an end-user can scroll the widget content swiping it up or down. Applies only if useNative is false
     */
    
    get scrollByContent(): boolean {
        return this._getOption('scrollByContent');
    }
    set scrollByContent(value: boolean) {
        this._setOption('scrollByContent', value);
    }


    /**
     * A Boolean value specifying whether or not an end-user can scroll the widget content using the scrollbar.
     */
    
    get scrollByThumb(): boolean {
        return this._getOption('scrollByThumb');
    }
    set scrollByThumb(value: boolean) {
        this._setOption('scrollByThumb', value);
    }


    /**
     * Specifies when the widget shows the scrollbar.
     */
    
    get showScrollbar(): string {
        return this._getOption('showScrollbar');
    }
    set showScrollbar(value: string) {
        this._setOption('showScrollbar', value);
    }


    /**
     * Indicates whether to use native or simulated scrolling.
     */
    
    get useNative(): boolean {
        return this._getOption('useNative');
    }
    set useNative(value: boolean) {
        this._setOption('useNative', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the pullDown event.
     */
     onPullDown: EventEmitter<any>;

    /**
     * A handler for the reachBottom event.
     */
     onReachBottom: EventEmitter<any>;

    /**
     * A handler for the scroll event.
     */
     onScroll: EventEmitter<any>;

    /**
     * A handler for the update event.
     */
     onUpdated: EventEmitter<any>;

    /**
     * A handler for the bounceEnabledChange event.
     */
     bounceEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the directionChange event.
     */
     directionChange: EventEmitter<string>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the pulledDownTextChange event.
     */
     pulledDownTextChange: EventEmitter<string>;

    /**
     * A handler for the pullingDownTextChange event.
     */
     pullingDownTextChange: EventEmitter<string>;

    /**
     * A handler for the reachBottomTextChange event.
     */
     reachBottomTextChange: EventEmitter<string>;

    /**
     * A handler for the refreshingTextChange event.
     */
     refreshingTextChange: EventEmitter<string>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the scrollByContentChange event.
     */
     scrollByContentChange: EventEmitter<boolean>;

    /**
     * A handler for the scrollByThumbChange event.
     */
     scrollByThumbChange: EventEmitter<boolean>;

    /**
     * A handler for the showScrollbarChange event.
     */
     showScrollbarChange: EventEmitter<string>;

    /**
     * A handler for the useNativeChange event.
     */
     useNativeChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;





    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            _watcherHelper: WatcherHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'pullDown', emit: 'onPullDown' },
            { subscribe: 'reachBottom', emit: 'onReachBottom' },
            { subscribe: 'scroll', emit: 'onScroll' },
            { subscribe: 'updated', emit: 'onUpdated' },
            { emit: 'bounceEnabledChange' },
            { emit: 'directionChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'heightChange' },
            { emit: 'pulledDownTextChange' },
            { emit: 'pullingDownTextChange' },
            { emit: 'reachBottomTextChange' },
            { emit: 'refreshingTextChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'scrollByContentChange' },
            { emit: 'scrollByThumbChange' },
            { emit: 'showScrollbarChange' },
            { emit: 'useNativeChange' },
            { emit: 'widthChange' }
        ]);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxScrollView(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }


static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-scroll-view',
    template: '<ng-content></ng-content>',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'bounceEnabled': [{ type: Input },],
'direction': [{ type: Input },],
'disabled': [{ type: Input },],
'elementAttr': [{ type: Input },],
'height': [{ type: Input },],
'pulledDownText': [{ type: Input },],
'pullingDownText': [{ type: Input },],
'reachBottomText': [{ type: Input },],
'refreshingText': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'scrollByContent': [{ type: Input },],
'scrollByThumb': [{ type: Input },],
'showScrollbar': [{ type: Input },],
'useNative': [{ type: Input },],
'width': [{ type: Input },],
'onDisposing': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onPullDown': [{ type: Output },],
'onReachBottom': [{ type: Output },],
'onScroll': [{ type: Output },],
'onUpdated': [{ type: Output },],
'bounceEnabledChange': [{ type: Output },],
'directionChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'pulledDownTextChange': [{ type: Output },],
'pullingDownTextChange': [{ type: Output },],
'reachBottomTextChange': [{ type: Output },],
'refreshingTextChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'scrollByContentChange': [{ type: Output },],
'scrollByThumbChange': [{ type: Output },],
'showScrollbarChange': [{ type: Output },],
'useNativeChange': [{ type: Output },],
'widthChange': [{ type: Output },],
};
}


export class DxScrollViewModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxTemplateModule
  ],
  declarations: [
    DxScrollViewComponent
  ],
  exports: [
    DxScrollViewComponent,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
